/** STAFF SET THE TABLE OR SEAT NUMBER.
 *
 *  Players cannot register until a member of staff has named the table. Staff
 *  identify themselves with their OWN 4-digit code — the same staff
 *  identification the voucher Verify and Redeem screens use — then pick a table
 *  from the list or type any label ("Bar seat 4"). The same code changes or
 *  clears it at any time, and every set, change and clear is logged.
 *
 *  The lock switch, who set it, who locked it and the audit lines arrive ONLY
 *  in the answer to a correct code: a player's phone never receives a staff
 *  name. Five wrong codes pause this card's staff attempts for 15 minutes.
 *
 *  A DEMO venue never sees this: its number fills itself in.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Lock } from 'lucide-react';
import api from '../../lib/api';

export const GoalStaffTable = ({ cardId, label, onDone, c, field, primaryBtn, ghostBtn }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [pin, setPin] = useState('');
    const [choices, setChoices] = useState([]);
    const [picked, setPicked] = useState('');
    const [other, setOther] = useState('');
    const [log, setLog] = useState([]);
    const [locked, setLocked] = useState(false);
    const [verified, setVerified] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const failure = (e) => {
        const detail = e.response?.data?.detail;
        if (detail === 'staff_code_locked') return t('goalGame.staffTooManyTries');
        if (detail === 'invalid_pin' || detail === 'pin_four_digits') {
            return t('goalGame.staffInvalidPin');
        }
        return t('goalGame.staffSaveFailed');
    };

    useEffect(() => {
        if (!open) return;
        (async () => {
            try {
                const res = await api.get(`/goal-game/public/${cardId}/table/number`);
                setChoices(res.data.choices || []);
            } catch (_e) { setChoices([]); }
        })();
    }, [open, cardId]);

    /* The staff view — the lock's position and the audit lines — is handed over
       only once the code is right. */
    useEffect(() => {
        if (!open || pin.length !== 4) { setVerified(false); return undefined; }
        let dead = false;
        (async () => {
            try {
                const res = await api.post(
                    `/goal-game/public/${cardId}/table/staff-view`, { pin });
                if (dead) return;
                setChoices(res.data.choices || []);
                setLog(res.data.log || []);
                setLocked(!!res.data.locked);
                setVerified(true);
                setError('');
            } catch (e) {
                if (dead) return;
                setVerified(false);
                setError(failure(e));
            }
        })();
        return () => { dead = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, pin, cardId]);

    const close = () => {
        setOpen(false); setPin(''); setPicked(''); setOther(''); setError('');
        setVerified(false); setLog([]);
    };

    const save = async (event) => {
        event.preventDefault();
        setError('');
        const value = (picked === 'other' ? other : picked).trim();
        if (!value) { setError(t('goalGame.staffPickTable')); return; }
        setBusy(true);
        try {
            const res = await api.post(`/goal-game/public/${cardId}/table/number`,
                { pin, label: value });
            setLog(res.data.log || []);
            close();
            if (onDone) await onDone();
        } catch (e) {
            setError(failure(e));
        } finally { setBusy(false); }
    };

    const clear = async () => {
        setError('');
        setBusy(true);
        try {
            const res = await api.post(`/goal-game/public/${cardId}/table/number/clear`, { pin });
            setLog(res.data.log || []);
            close();
            if (onDone) await onDone();
        } catch (e) {
            setError(failure(e));
        } finally { setBusy(false); }
    };

    /** Lock this card to the table it is showing — or let it go again. */
    const setLock = async (next) => {
        setError('');
        setBusy(true);
        try {
            const res = await api.post(`/goal-game/public/${cardId}/table/number/lock`,
                { pin, locked: next });
            setLog(res.data.log || []);
            setLocked(!!res.data.locked);
            if (onDone) await onDone();
        } catch (e) {
            setError(failure(e));
        } finally { setBusy(false); }
    };

    if (!open) {
        return (
            <div className="space-y-2">
                {/* Nothing about the lock is shown to players: the closed state
                    is only the staff button. */}
                <button type="button" onClick={() => setOpen(true)}
                        data-testid="goal-staff-open"
                        style={label ? ghostBtn : primaryBtn}
                        className={label
                            ? 'w-full rounded-xl border border-white/25 py-2.5 text-sm font-semibold'
                            : 'w-full rounded-xl font-bold py-3 shadow-lg'}>
                    {label ? t('goalGame.staffChangeTable') : t('goalGame.staffSetTable')}
                </button>
            </div>
        );
    }

    return (
        <form onSubmit={save} className="space-y-3 text-left" data-testid="goal-staff-form">
            {label && (
                <p className="text-xs font-semibold flex items-center gap-1.5 opacity-80"
                   data-testid="goal-staff-current">
                    {verified && locked && <Lock size={12} data-testid="goal-staff-lock-icon" />}
                    {label}
                </p>
            )}
            <label className="block">
                <span className="block text-xs opacity-60 mb-1">{t('goalGame.staffPinLabel')}</span>
                <input type="tel" inputMode="numeric" autoComplete="off" maxLength={4}
                       value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                       className={`${field} tracking-[0.4em] text-center`}
                       data-testid="goal-staff-pin" />
            </label>
            <div>
                <span className="block text-xs opacity-60 mb-1">{t('goalGame.staffPickTable')}</span>
                <select value={picked} onChange={(e) => setPicked(e.target.value)}
                        className={field} data-testid="goal-staff-choice">
                    <option value="">—</option>
                    {choices.map((choice) => (
                        <option key={choice} value={choice}>{choice}</option>
                    ))}
                    <option value="other">{t('goalGame.staffOther')}</option>
                </select>
            </div>
            {picked === 'other' && (
                <input type="text" value={other} maxLength={40}
                       onChange={(e) => setOther(e.target.value)}
                       placeholder={t('goalGame.staffOtherLabel')}
                       className={field} data-testid="goal-staff-other" />
            )}
            {error && <p className="text-sm text-red-300" data-testid="goal-staff-error">{error}</p>}
            {/* Lock this card to the table it is showing. One tap on, one tap
                off, with the same code. Nothing else about the table changes. */}
            {label && verified && (
                <label className="flex items-center justify-between gap-3 rounded-xl border border-white/15 px-3 py-2.5"
                       data-testid="goal-staff-lock-row">
                    <span className="text-sm font-semibold flex items-center gap-1.5">
                        <Lock size={14} />
                        {t('goalGame.staffLockCard', { label })}
                    </span>
                    <input type="checkbox" checked={locked} disabled={busy}
                           onChange={(e) => setLock(e.target.checked)}
                           data-testid="goal-staff-lock" className="w-5 h-5" />
                </label>
            )}
            {label && !verified && (
                <p className="text-[11px] opacity-60" data-testid="goal-staff-lock-hint">
                    {t('goalGame.staffLockHint')}
                </p>
            )}
            {verified && locked && (
                <p className="text-[11px] opacity-60" data-testid="goal-staff-locked-note">
                    {t('goalGame.staffLockedNote')}
                </p>
            )}
            <button type="submit" disabled={busy || !verified || locked}
                    style={primaryBtn} data-testid="goal-staff-save"
                    className="w-full rounded-xl font-bold py-3 shadow-lg disabled:opacity-50">
                {busy ? <Loader2 className="animate-spin mx-auto" size={20} /> : t('goalGame.staffSave')}
            </button>
            <div className="flex gap-2">
                <button type="button" onClick={close} disabled={busy} style={ghostBtn}
                        data-testid="goal-staff-cancel"
                        className="flex-1 rounded-xl border border-white/25 py-2.5 text-sm font-semibold">
                    {t('goalGame.back')}
                </button>
                {label && (
                    <button type="button" onClick={clear} disabled={busy || !verified || locked}
                            style={ghostBtn} data-testid="goal-staff-clear"
                            className="flex-1 rounded-xl border border-white/25 py-2.5 text-sm font-semibold disabled:opacity-50">
                        {t('goalGame.staffClear')}
                    </button>
                )}
            </div>
            {log.length > 0 && (
                <div className="space-y-0.5 pt-1" data-testid="goal-staff-log">
                    {log.slice(0, 3).map((line) => (
                        <p key={line.id} className="text-[11px] opacity-60">
                            {line.label || line.previous_label} · {line.action} ·{' '}
                            {line.staff_name || '—'}
                        </p>
                    ))}
                </div>
            )}
        </form>
    );
};

export default GoalStaffTable;
